import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Upload, FileText, User, Briefcase, Bell, LogOut, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700",
  shortlisted: "bg-blue-100 text-blue-700",
  interview: "bg-amber-100 text-amber-700",
  offered: "bg-purple-100 text-purple-700",
  joined: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
  on_hold: "bg-gray-100 text-gray-700",
};

export default function CandidateDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState({});
  const [apps, setApps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const [p, a] = await Promise.all([api.get("/profile"), api.get("/applications/mine")]);
    setProfile(p.data || {});
    setApps(a.data);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...profile,
        skills: typeof profile.skills === "string" ? profile.skills.split(",").map(s => s.trim()).filter(Boolean) : profile.skills,
        languages: typeof profile.languages === "string" ? profile.languages.split(",").map(s => s.trim()).filter(Boolean) : profile.languages,
      };
      await api.put("/profile", payload);
      toast.success("Profile saved");
      load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const uploadResume = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/profile/upload-resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded");
      setProfile({ ...profile, resume_path: r.data.path, resume_filename: r.data.filename });
    } catch { toast.error("Upload failed"); }
  };

  const parseAI = async () => {
    if (!aiText.trim()) return toast.info("Paste your resume text first");
    setAiLoading(true);
    try {
      const r = await api.post("/ai/parse-resume", { text: aiText });
      const p = r.data.parsed || {};
      setProfile({ ...profile, ...p });
      toast.success("AI extracted profile fields — review & save");
    } catch { toast.error("Parsing failed"); } finally { setAiLoading(false); }
  };

  const setF = (k, v) => setProfile({ ...profile, [k]: v });
  const skillsStr = Array.isArray(profile.skills) ? profile.skills.join(", ") : (profile.skills || "");
  const langStr = Array.isArray(profile.languages) ? profile.languages.join(", ") : (profile.languages || "");

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="candidate-dashboard">
      <section className="bg-[#0F2A4A] text-white py-14">
        <div className="ae-container flex flex-col md:flex-row justify-between gap-4">
          <div>
            <div className="ae-overline mb-2">Candidate Portal</div>
            <h1 className="text-3xl md:text-4xl font-light">Welcome, <span className="font-serif italic text-[#C5A059]">{user.full_name}</span></h1>
          </div>
          <button onClick={() => { logout(); nav("/"); }} className="text-white/70 hover:text-white text-sm flex items-center gap-2 self-start" data-testid="cand-logout">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </section>

      <div className="ae-container py-8">
        <div className="flex gap-2 mb-6 border-b border-slate-200">
          {[
            { k: "profile", l: "My Profile", icon: User },
            { k: "resume", l: "Resume", icon: FileText },
            { k: "applications", l: `Applications (${apps.length})`, icon: Briefcase },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${tab === t.k ? "border-[#C5A059] text-[#0F2A4A]" : "border-transparent text-slate-500 hover:text-[#0F2A4A]"}`}>
              <t.icon className="w-4 h-4" /> {t.l}
            </button>
          ))}
        </div>

        {tab === "profile" && (
          <div className="ae-card p-8 space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-heading font-medium text-xl text-[#0F2A4A]">Personal Details</h2>
              <button onClick={save} disabled={saving} className="ae-btn-primary text-sm" data-testid="save-profile">
                {saving ? "Saving…" : "Save Profile"}
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Full Name"><Inp val={profile.full_name} onChange={v => setF("full_name", v)} tid="p-name" /></Field>
              <Field label="Headline"><Inp val={profile.headline} onChange={v => setF("headline", v)} placeholder="e.g., Senior Software Engineer" tid="p-headline" /></Field>
              <Field label="Phone"><Inp val={profile.phone} onChange={v => setF("phone", v)} tid="p-phone" /></Field>
              <Field label="Industry"><Inp val={profile.industry} onChange={v => setF("industry", v)} tid="p-industry" /></Field>
              <Field label="Current Location"><Inp val={profile.current_location} onChange={v => setF("current_location", v)} tid="p-loc" /></Field>
              <Field label="Preferred Location"><Inp val={profile.preferred_location} onChange={v => setF("preferred_location", v)} tid="p-preloc" /></Field>
              <Field label="Total Experience (years)"><Inp type="number" val={profile.total_experience} onChange={v => setF("total_experience", parseFloat(v) || 0)} tid="p-exp" /></Field>
              <Field label="Notice Period"><Inp val={profile.notice_period} onChange={v => setF("notice_period", v)} placeholder="e.g., 30 days" tid="p-notice" /></Field>
              <Field label="Current Salary (₹ p.a.)"><Inp type="number" val={profile.current_salary} onChange={v => setF("current_salary", parseFloat(v) || 0)} tid="p-cur-sal" /></Field>
              <Field label="Expected Salary (₹ p.a.)"><Inp type="number" val={profile.expected_salary} onChange={v => setF("expected_salary", parseFloat(v) || 0)} tid="p-exp-sal" /></Field>
              <Field label="LinkedIn"><Inp val={profile.linkedin} onChange={v => setF("linkedin", v)} tid="p-linkedin" /></Field>
              <Field label="Portfolio"><Inp val={profile.portfolio} onChange={v => setF("portfolio", v)} tid="p-portfolio" /></Field>
            </div>
            <Field label="Skills (comma-separated)">
              <Inp val={skillsStr} onChange={v => setF("skills", v)} tid="p-skills" placeholder="Python, React, AWS…" />
            </Field>
            <Field label="Languages (comma-separated)">
              <Inp val={langStr} onChange={v => setF("languages", v)} tid="p-lang" placeholder="English, Hindi…" />
            </Field>
            <Field label="Professional Summary">
              <textarea value={profile.summary || ""} onChange={e => setF("summary", e.target.value)}
                data-testid="p-summary" rows={4}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm" />
            </Field>
          </div>
        )}

        {tab === "resume" && (
          <div className="space-y-6">
            <div className="ae-card p-8">
              <h2 className="font-heading font-medium text-xl text-[#0F2A4A] mb-4">Resume Upload</h2>
              {profile.resume_path ? (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-[#0F2A4A] font-medium">{profile.resume_filename || "Resume uploaded"}</span>
                </div>
              ) : (
                <p className="text-sm text-slate-500 mb-3">No resume uploaded yet.</p>
              )}
              <label className="ae-btn-outline inline-flex items-center gap-2 cursor-pointer mt-4" data-testid="resume-upload-label">
                <Upload className="w-4 h-4" /> Upload PDF/DOC
                <input type="file" accept=".pdf,.doc,.docx" onChange={e => e.target.files[0] && uploadResume(e.target.files[0])} className="hidden" data-testid="resume-input" />
              </label>
            </div>

            <div className="ae-card p-8 bg-gradient-to-br from-[#C5A059]/5 to-transparent">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-[#C5A059]" />
                <h2 className="font-heading font-medium text-xl text-[#0F2A4A]">AI Resume Parser</h2>
              </div>
              <p className="text-sm text-slate-600 mb-4">Paste your resume text below and our AI will auto-fill your profile.</p>
              <textarea value={aiText} onChange={e => setAiText(e.target.value)} rows={8}
                data-testid="ai-resume-text"
                placeholder="Paste your resume text here…"
                className="w-full rounded-xl border border-slate-200 bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm mb-3" />
              <button onClick={parseAI} disabled={aiLoading} className="ae-btn-primary text-sm" data-testid="ai-parse-btn">
                {aiLoading ? "Parsing with AI…" : "Extract Profile with AI"}
              </button>
            </div>
          </div>
        )}

        {tab === "applications" && (
          <div className="space-y-3">
            {apps.length === 0 ? (
              <div className="ae-card p-12 text-center text-slate-500">No applications yet. Start browsing jobs!</div>
            ) : apps.map(a => (
              <div key={a.id} className="ae-card p-6 flex flex-col md:flex-row justify-between gap-4" data-testid={`app-${a.id}`}>
                <div>
                  <h3 className="font-heading font-medium text-[#0F2A4A]">{a.job_title}</h3>
                  <div className="text-sm text-slate-500 mt-1">{a.company_name}</div>
                  <div className="text-xs text-slate-400 mt-2">Applied {new Date(a.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex flex-col items-start md:items-end gap-2">
                  <span className={`text-xs px-3 py-1.5 rounded-full font-medium capitalize ${STATUS_COLORS[a.status]}`}>{a.status.replace("_", " ")}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">{label}</label>
    {children}
  </div>
);
const Inp = ({ val, onChange, type = "text", placeholder, tid }) => (
  <input type={type} value={val || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    data-testid={tid}
    className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm" />
);
